import { enqueueFeEvent } from './queue';
import type { FeActionEvent } from './types';

export type StudentActionType =
  | 'ASSIGNMENT_VIEW'
  | 'ASSIGNMENT_PRACTICE_START'
  | 'ASSIGNMENT_SUBMIT_SUCCESS'
  | 'OFFLINE_WORK_VIEW'
  | 'CLASSROOM_VIEW'
  | 'SUBMISSION_THREAD_VIEW';

export type StudentActionContext = {
  assignmentCode?: string;
  classroomCode?: string;
  pathname?: string;
  submissionId?: number;
  score?: number;
  metadata?: Record<string, unknown>;
};

const DEDUPE_MS = 5000;
const MAX_DEDUPE_KEYS = 100;
const recentByKey = new Map<string, number>();

function pruneDedupeMap(now: number) {
  if (recentByKey.size <= MAX_DEDUPE_KEYS) {
    return;
  }
  for (const [key, ts] of recentByKey) {
    if (now - ts >= DEDUPE_MS) {
      recentByKey.delete(key);
    }
  }
}

function shouldSend(action: string, context: StudentActionContext): boolean {
  const key = [
    action,
    context.assignmentCode ?? '',
    context.classroomCode ?? '',
    context.pathname ?? '',
    context.submissionId ?? '',
  ].join('|');
  const now = Date.now();
  pruneDedupeMap(now);
  const last = recentByKey.get(key);
  if (last != null && now - last < DEDUPE_MS) {
    return false;
  }
  recentByKey.set(key, now);
  return true;
}

export function enqueueStudentAction(
  name: FeActionEvent['name'] | string,
  params: { asn?: string; cls?: string; meta?: Record<string, unknown> } = {},
) {
  enqueueFeEvent({
    k: 'act',
    t: new Date().toISOString(),
    name,
    asn: params.asn,
    cls: params.cls,
    meta: params.meta,
  });
}

/** Semantic student actions — batched to /api/client-telemetry/events as fe act events. */
export function reportStudentAction(action: StudentActionType, context: StudentActionContext = {}) {
  if (typeof window === 'undefined') {
    return;
  }
  if (!shouldSend(action, context)) {
    return;
  }

  enqueueStudentAction(action, {
    asn: context.assignmentCode,
    cls: context.classroomCode,
    meta: {
      pathname: context.pathname ?? window.location.pathname,
      submissionId: context.submissionId,
      score: context.score,
      ...(context.metadata ?? {}),
    },
  });
}
